import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { Snake } from "@/components/decor/SvgIcons";

const links = [
  { to: "/", label: "Home" },
  { to: "/booking", label: "Booking" },
  { to: "/animals", label: "Meet Our Animals" },
  { to: "/adoptables", label: "Adoptables" },
  { to: "/resources", label: "Husbandry Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div
          className={`flex items-center justify-between rounded-full border transition-all duration-500 ${
            scrolled
              ? "bg-[#F8F4EA]/90 backdrop-blur-xl border-sage/50 px-4 py-2 shadow-[0_10px_40px_-20px_rgba(41,67,50,0.35)]"
              : "bg-[#F8F4EA]/60 backdrop-blur-md border-sage/30 px-4 py-2"
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group pl-1">
            <img
              src="/brand/spooky-scales-logo.jpg"
              alt="Spooky Scales"
              className={`rounded-full object-cover border border-forest/25 shadow-sm group-hover:scale-105 transition-all duration-500 ${
                scrolled ? "w-14 h-14" : "w-[76px] h-[76px]"
              }`}
            />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-forest text-2xl md:text-3xl tracking-tight">Spooky Scales</span>
              <span className="font-script text-plum text-base -mt-0.5">Southern California</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition ${
                    isActive ? "bg-forest text-cream" : "text-forest hover:bg-sage/30"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/booking"
              className="hidden md:inline-flex btn-pill btn-primary text-sm"
              data-testid="nav-cta-book"
            >
              <Snake size={22} /> Book an Event
            </Link>
            <button
              className="lg:hidden p-2.5 rounded-full bg-cream border border-sage/60 text-forest"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden mt-2 rounded-3xl bg-[#F8F4EA]/95 backdrop-blur-xl border border-sage/50 p-4 shadow-xl"
              data-testid="nav-mobile-panel"
            >
              <div className="flex flex-col">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-2xl text-base font-medium ${
                        isActive ? "bg-forest text-cream" : "text-forest hover:bg-sage/30"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/booking"
                  className="mt-2 btn-pill btn-primary justify-center"
                >
                  Book an Event
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
